import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { selectors } from "reducers";
import {
  getBonafideInsuranceInfo,
  submitBonafideOrder
} from "features/bonafide/actions";
import BonafideOrderStatus from "./bonafide-order-status";
import BonafideOrderInfo from "./bonafide-order-info";
import BonafideFooter from "./bonafide-footer";
import AuthForm from "./auth-form";
import { getPendingOrderTasksHoldingOrder } from "features/tasks/helpers";
import { orderNeedsBranch } from "features/bonafide/helpers";
import { OrderProfileContext } from "components/order-profile-new/context";
import { formatDate } from "utils/dates";
import { useFetch } from "hooks";
import { get, post } from "utils/api";
import { errorMessage } from "actions/message";

export const ACCESS_DENIED = "Access denied.";

const BonafidePanel = ({
  visible,
  getBonafideInsuranceInfo,
  submitBonafideOrder,
  orderId,
  verificationHistory,
  errorMessage
}) => {
  const {
    tasks,
    order,
    fetchOrderLineItemsApi,
    getPatientTasks,
    fetchOrderProfile
  } = React.useContext(OrderProfileContext);

  const patientId = order.patient_id;
  const orderLineItems = fetchOrderLineItemsApi.response;

  const hasPendingTask = getPendingOrderTasksHoldingOrder(tasks, orderId) > 0;
  const [isFetching, setIsFetching] = React.useState(false);
  const [editAuthScreen, setEditAuth] = React.useState(false);
  const [index, setIndex] = React.useState(0);
  const initData = async () => {
    await getBonafideInsuranceInfo(orderId);
  };

  React.useEffect(() => {
    initData();
    setIndex(0);
  }, [orderId]);

  // S3DEV2-7704: a failed load stores { error: status } instead of the history array.
  const loadError = !Array.isArray(verificationHistory)
    ? verificationHistory?.error
    : null;
  const history = Array.isArray(verificationHistory) ? verificationHistory : [];

  const handleBonafideError = error =>
    errorMessage(
      error?.status === 403 ? ACCESS_DENIED : "Bonafide request failed."
    );

  const bonafideLastResult = history[0] ?? {};
  const bonafideSelectedResult = history[index] ?? {};

  let currentStep = 1;
  if (bonafideLastResult?.isConfirmed === true && bonafideLastResult?.response?.status != "failed" && bonafideLastResult?.response?.status != "error") {
    currentStep = 3;
  } else if (bonafideLastResult?.response) {
    currentStep = 2;
  }

  const { fetchData: checkCmnNeeded } = useFetch({
    apiFn: ({ orderId }) => post(`bonafide/check-cmn-needed`, { orderId }),
    throwOnError: true
  });

  // S3DEV2-7469: warn before sending an order to Bonafide when the patient is not assigned to a
  // branch. Only relevant when the company actually has active branches configured — companies with
  // no branches (e.g. MedSouth, where each location is its own company) send under the company
  // account number and must still go through, so needsBranch stays false for them. FE is convenience
  // only; the authoritative guard is BonafideController.ShouldBlockForMissingOffice, which also
  // verifies the resolved branch carries an account number. A failed branches fetch leaves the
  // buttons enabled.
  const companyBranchesApi = useFetch({
    apiFn: companyId => get(`companies/${companyId}/branches`),
    defaultValue: []
  });

  React.useEffect(() => {
    if (order.company_id) companyBranchesApi.fetchData(order.company_id);
  }, [order.company_id]);

  const needsBranch = orderNeedsBranch({
    branches: companyBranchesApi.response,
    order
  });

  const branchWarning =
    "This patient is not assigned to a branch/office. Assign the patient to a branch before sending order for processing.";

  if (loadError === 403)
    return (
      <div className={`bonafide-panel ${visible ? "" : "hidden"}`}>
        <div
          className="bonafide-access-denied"
          style={{ padding: 20, fontWeight: "bold", color: "#666" }}
        >
          {ACCESS_DENIED}
        </div>
      </div>
    );

  return (
    <div className={`bonafide-panel ${visible ? "" : "hidden"}`}>
      <div
        className="bonafide-panel-header"
        style={{
          padding: 5,
          display: "grid",
          gridTemplateColumns: "auto 1fr",
          gridGap: 10,
          alignItems: "center",
          borderBottom: "1px solid #ccc"
        }}
      >
        <label>History: </label>
        <select onChange={e => setIndex(e.target.value)} style={{ margin: 0 }}>
          {history.map((x, index) => (
            <>
              {index == 1 && (
                <option disabled key={index}>
                  ----------------------------------------------------------------
                </option>
              )}
              <option key={index + x.id} value={index}>
                {formatDate(x.requestedAt, "L hh:mm A")} -{" "}
                {x.isConfirmed ? "Order Submit" : "Order Verification"} -{" "}
                {x.response.status}{" "}
              </option>
            </>
          ))}
        </select>
      </div>
      {editAuthScreen ? (
        <AuthForm
          orderLineItems={orderLineItems}
          currentStep={currentStep}
          canOverrideOrderCreation={currentStep == 1}
          handleCancel={() => setEditAuth(false)}
          hasPendingTask={hasPendingTask}
          submitOrder={async () => {
            try {
              setEditAuth(false);
              if (isFetching) return;
              setIsFetching(true);
              const orderSummary = document.getElementById(
                `copyArea-${orderId}`
              );
              await submitBonafideOrder(
                orderId,
                orderSummary.innerText,
                true,
                true
              );
              fetchOrderProfile(orderId);
            } catch (error) {
              handleBonafideError(error);
            } finally {
              setIsFetching(false);
            }
          }}
        />
      ) : (
        <div className="bonafide-result-container">
          <BonafideOrderStatus
            response={bonafideSelectedResult?.response}
            requestedAt={bonafideSelectedResult.requestedAt}
            requestedBy={bonafideSelectedResult.requestedBy}
          />
          <BonafideOrderInfo
            response={bonafideSelectedResult.response}
            responseBody={bonafideSelectedResult.responseBody}
          />
        </div>
      )}
      <BonafideFooter
        isFetching={isFetching}
        hasPendingTask={hasPendingTask}
        needsBranch={needsBranch}
        canOverrideOrderCreation={currentStep == 1}
        currentStep={currentStep}
        bonafideSelectedResult={bonafideSelectedResult}
        handleRefresh={async () => {
          try {
            setIsFetching(true);
            await getBonafideInsuranceInfo(orderId);
          } catch (error) {
            handleBonafideError(error);
          } finally {
            setIsFetching(false);
          }
        }}
        handleCreatePreOrder={async () => {
          try {
            if (isFetching) return;
            setIsFetching(true);

            const cmnTaskAdded = await checkCmnNeeded({ orderId });
            if (cmnTaskAdded) await getPatientTasks(patientId);

            const orderSummary = document.getElementById(`copyArea-${orderId}`);
            await submitBonafideOrder(orderId, orderSummary.innerText, false);
            fetchOrderProfile(orderId);
          } catch (error) {
            handleBonafideError(error);
          } finally {
            setIsFetching(false);
          }
        }}
        handleCreateOrderWithAuth={() => {
          if (hasPendingTask) {
            alert(
              "Order has pending task, resolve hold before sending order for processing"
            );
            return;
          }
          if (needsBranch) {
            alert(branchWarning);
            return;
          }
          setEditAuth(true);
        }}
        handleCreateOrder={async () => {
          if (hasPendingTask) {
            alert(
              "Order has pending task, resolve hold before sending order for processing"
            );
            return;
          } else if (needsBranch) {
            alert(branchWarning);
            return;
          } else {
            try {
              setEditAuth(false);
              if (isFetching) return;
              setIsFetching(true);
              const orderSummary = document.getElementById(
                `copyArea-${orderId}`
              );
              await submitBonafideOrder(
                orderId,
                orderSummary.innerText,
                true,
                false
              );
              fetchOrderProfile(orderId);
            } catch (error) {
              handleBonafideError(error);
            } finally {
              setIsFetching(false);
            }
          }
        }}
        editAuthScreen={editAuthScreen}
      />
    </div>
  );
};

BonafidePanel.propTypes = {
  orderId: PropTypes.string.isRequired,
  getBonafideInsuranceInfo: PropTypes.func.isRequired,
  submitBonafideOrder: PropTypes.func.isRequired,
  canOverrideOrderCreation: PropTypes.bool,
  verificationHistory: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.object),
    PropTypes.shape({
      error: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.bool,
        PropTypes.string
      ])
    })
  ]),
  errorMessage: PropTypes.func.isRequired,
  visible: PropTypes.bool
};

export default connect(
  (state, { orderId }) => {
    return {
      verificationHistory: selectors.getBonafideOrderResult(state, { orderId })
    };
  },
  {
    getBonafideInsuranceInfo,
    submitBonafideOrder,
    errorMessage
  }
)(BonafidePanel);
